import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  Button,
  Pagination,
  Panel,
  Toggle,
  colors,
} from 'src/components/app-ui'
import CreateCourseButton from 'src/components/create-course'
import { editCourseCollectionIndexAction } from 'src/reducers/collection/action'
import { courseGetSuccess } from 'src/reducers/course/action'
import { clearCardState } from 'src/reducers/card/action'
import { useSettingField } from '../hooks/usePathSettings'
import CourseRow from './course-row'
import AddCoursesDialog from './add-courses-dialog'
import { coursePath, editorPath } from 'src/pages/course-detail/tabs'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sequential: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '14px 20px',
  },
  sequentialText: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sequentialIcon: {
    width: 18,
    height: 18,
    flex: 'none',
    color: colors.primary,
  },
  title: {
    fontSize: '14px',
    fontWeight: 500,
  },
  desc: {
    fontSize: '13px',
    color: colors.muted,
  },
  list: {
    overflow: 'hidden',
  },
  empty: {
    padding: '28px 16px',
    borderBottom: '1px solid #F1F3F5',
    textAlign: 'center',
    fontSize: '14px',
    color: colors.muted,
  },
  pagination: {
    borderBottom: `1px solid ${colors.border}`,
  },
  footer: {
    display: 'flex',
    gap: '10px',
    padding: '14px 16px',
  },
})

const CoursesTab = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const courses = useSelector(state => state.collection.courses)
  const [isSequential, setSequential] = useSettingField('isSequential')
  const [localCourses, setLocalCourses] = useState([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const pathName = useSelector(state => state.collection.collection?.name)

  useEffect(() => {
    setLocalCourses(courses || [])
  }, [courses])

  // Stay on a valid page when courses are removed.
  const lastPage = Math.max(0, Math.ceil(localCourses.length / pageSize) - 1)
  useEffect(() => {
    if (page > lastPage) setPage(lastPage)
  }, [page, lastPage])

  const pageStart = page * pageSize
  const pageCourses = localCourses.slice(pageStart, pageStart + pageSize)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const from = localCourses.findIndex(course => course.id === active.id)
    const to = localCourses.findIndex(course => course.id === over.id)
    const reordered = arrayMove(localCourses, from, to).map(
      (course, index) => ({
        ...course,
        courseCollectionOrder: index,
        courseCollectionId: id,
      }),
    )
    setLocalCourses(reordered)
    dispatch(editCourseCollectionIndexAction({ courseDtos: reordered }))
  }

  const openCourse = (course, inEditor = false) => {
    dispatch(clearCardState())
    dispatch(courseGetSuccess(null))
    history.push(inEditor ? editorPath(course.id) : coursePath(course.id))
    window.scrollTo(0, 0)
  }

  return (
    <div className={classes.root}>
      <Panel className={classes.sequential}>
        <div className={classes.sequentialText}>
          <Icon icon="tabler:arrows-sort" className={classes.sequentialIcon} />
          <div>
            <div className={classes.title}>Sequential progression</div>
            <div className={classes.desc}>
              Learners complete courses in this order. The next course unlocks
              after the previous one is finished.
            </div>
          </div>
        </div>
        <Toggle checked={Boolean(isSequential)} onChange={setSequential} />
      </Panel>

      <Panel className={classes.list}>
        {localCourses.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pageCourses.map(course => course.id)}
              strategy={verticalListSortingStrategy}
            >
              {pageCourses.map((course, index) => (
                <CourseRow
                  key={course.id}
                  course={course}
                  step={pageStart + index + 1}
                  locked={isSequential && pageStart + index > 0}
                  onOpen={() => openCourse(course)}
                  onEdit={() => openCourse(course, true)}
                  localCourses={localCourses}
                  setLocalCourses={setLocalCourses}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <div className={classes.empty}>
            This path has no courses yet. Add an existing course or create a new
            one.
          </div>
        )}
        {localCourses.length > 0 && (
          <Pagination
            className={classes.pagination}
            total={localCourses.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPageSize(size)
              setPage(0)
            }}
          />
        )}
        <div className={classes.footer}>
          <Button
            variant="outline"
            size="small"
            icon="tabler:plus"
            onClick={() => setPickerOpen(true)}
          >
            Add existing course
          </Button>
          <CreateCourseButton variant="path" pathId={id} pathName={pathName} />
        </div>
      </Panel>

      <AddCoursesDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        pathId={id}
        nextOrder={localCourses.length}
      />
    </div>
  )
}

export default CoursesTab
